import { mutate } from './api'
import { agreedTimeKey, timeIsAgreed } from './rebook-message'
import { visibleTime } from './business-settings'
import { shownTime } from './round-config'
import { undoPatch, type UndoLink } from './undo'
import { buzz } from './haptic'
import { addDaysISO, clear, dayLabel, el, todayISO } from './ui'
import type { Clean } from './types'
import './they-replied.css'

// "They replied", from the visit itself (Liam, 12 Oct 2026, Sarah and Ian Pendrigh asked about Tuesday at 10am: "they
// couldn't do it ... I'm not sure what the flow is to now move them on the diary. It's not particularly clear"; he
// approved the drawing at https://claude.ai/artifact/BihDdaN7wFtxmfsERNGQ49). A visit asked about its day with no reply
// yet has "They replied" as its lemon button, on the phone and the computer. It asks what they said:
//   Yes, <that day>   the day agreed (and the time, when it has one), one save, Undo;
//   Another day       the next three weeks as day buttons (or any date): the visit moves there, agreed, in ONE save with
//                     one Undo, keeping its time unless unticked; no message goes, they told you;
//   Not now           Messages' own reply card (Ask again later, Pause, No longer needs cleaning).
// Dragging such a visit onto another day asks first (askAgreedFirst): did they agree that day? Yes is the same save as
// Another day; No moves it as before and they are asked.

export interface RepliedOptions {
  changed: () => void            // the screen's redraw once a save lands (and after its Undo)
  follow?: (iso: string) => void // the screen goes to the day the visit went to (and back with Undo)
  notNow: () => void             // opens Messages' reply card for them
  // "Priya out" / "Nobody out yet", for the days the screen has read; "Working" / "Day off" in a one-person round (Liam,
  // 19 Oct 2026), never a name.
  dayInfo?: (iso: string) => string | null
  tellThem?: UndoLink            // "Send a confirmation" beside Undo, when they want one
}

const firstNames = (cl: Clean): string => (cl.customer_name ?? 'They').trim() || 'They'
const shortDay = (iso: string): string => new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })

// The day they were asked about, agreed (and its time). The same fields Mark confirmed and Time agreed save.
export function agreeAsAsked(cl: Clean, o: RepliedOptions): void {
  const time = visibleTime(cl)
  const timeKey = time ? agreedTimeKey(cl.date, time) : null
  const was = { confirmed: cl.confirmed ?? 0, time_agreed_for: timeIsAgreed(cl) ? cl.time_agreed_for ?? null : null }
  void mutate({
    label: `They replied, yes: ${cl.customer_name ?? ''} ${cl.date}`,
    method: 'PATCH',
    path: `/api/cleans/${cl.id}`,
    body: { version: cl.version, confirmed: 1, ...(timeKey ? { time_agreed_for: timeKey } : {}) },
    onAck: o.changed,
    onConflict: o.changed,
  }).then((ack) => {
    if (!ack.ok && ack.status >= 400 && ack.status < 500) { window.alert(ack.error ?? 'That did not save.'); o.changed(); return }
    undoPatch({ text: `${firstNames(cl)}: ${dayLabel(cl.date)} agreed${timeKey ? `, ${shownTime(time ?? '')}` : ''}`, label: `Undo agreed: ${cl.customer_name ?? ''} ${cl.date}`,
      path: `/api/cleans/${cl.id}`, ack, version: cl.version, body: was, after: o.changed, ...(o.tellThem ? { link: o.tellThem } : {}) })
  })
}

// Moved to the day they agreed, agreed, in one save with one Undo that puts the visit back exactly as it was.
export function agreeOnDay(cl: Clean, iso: string, keepTime: boolean, o: RepliedOptions): void {
  const time = visibleTime(cl)
  const timeKey = time && keepTime ? agreedTimeKey(iso, time) : null
  const was = { date: cl.date, confirmed: cl.confirmed ?? 0, time_slot: cl.time_slot ?? null, time_agreed_for: timeIsAgreed(cl) ? cl.time_agreed_for ?? null : null }
  void mutate({
    label: `They replied, ${dayLabel(iso)}: ${cl.customer_name ?? ''}`,
    method: 'PATCH',
    path: `/api/cleans/${cl.id}`,
    body: { version: cl.version, date: iso, confirmed: 1, ...(timeKey ? { time_agreed_for: timeKey } : time ? { time_slot: null, time_agreed_for: null } : {}) },
    onAck: (ack) => { if (ack.ok) o.follow?.(iso); o.changed() },
    onConflict: o.changed,
  }).then((ack) => {
    if (!ack.ok && ack.status >= 400 && ack.status < 500) { window.alert(ack.error ?? 'That did not save.'); o.changed(); return }
    undoPatch({ text: `${firstNames(cl)} moved to ${dayLabel(iso)}, agreed`, label: `Undo move: ${cl.customer_name ?? ''} back to ${dayLabel(cl.date)}`,
      path: `/api/cleans/${cl.id}`, ack, version: cl.version, body: { ...was, undo_restore: true }, onAck: (back) => { if (back.ok) o.follow?.(cl.date) }, after: o.changed, ...(o.tellThem ? { link: o.tellThem } : {}) })
  })
}

function sheet(label: string): { dialog: HTMLDialogElement; body: HTMLElement; close: () => void } {
  const dialog = el('dialog', { class: 'booking-dialog they-replied', 'aria-label': label }) as HTMLDialogElement
  const shut = el('button', { type: 'button', 'aria-label': 'Close' }, 'Close')
  const body = el('div', { class: 'they-replied-body' })
  dialog.append(el('header', { class: 'booking-head' }, el('h2', {}, label), shut), body)
  const close = (): void => { if (dialog.open) dialog.close() }
  shut.addEventListener('click', close)
  dialog.addEventListener('close', () => dialog.remove())
  document.body.append(dialog)
  dialog.showModal()
  return { dialog, body, close }
}

const bigButton = (words: string, small: string, main = false): HTMLButtonElement =>
  el('button', { type: 'button', class: `they-replied-choice${main ? ' is-main' : ''}` }, el('strong', {}, words), el('small', {}, small)) as HTMLButtonElement

export function openTheyReplied(cl: Clean, o: RepliedOptions): void {
  const time = visibleTime(cl)
  const s = sheet(`${firstNames(cl)} replied`)
  // Kept while days are tapped: unticked, it stays unticked (review: it came back on every day tapped).
  let keep = true
  const draw = (): void => {
    clear(s.body)
    s.body.append(el('p', { class: 'they-replied-about' }, `About ${dayLabel(cl.date)}${time ? `, ${shownTime(time ?? '')}` : ''}`))
    const yes = bigButton(`Yes, ${shortDay(cl.date)}`, time ? 'Day and time agreed' : 'Day agreed', true)
    yes.addEventListener('click', () => { s.close(); agreeAsAsked(cl, o) })
    const other = bigButton('Another day', 'Move them and mark it agreed')
    other.addEventListener('click', () => drawDays(null))
    const not = bigButton('Not now', 'Ask again later, pause, or no longer')
    not.addEventListener('click', () => { s.close(); o.notNow() })
    s.body.append(yes, other, not)
    yes.focus({ preventScroll: true })
  }
  // The next three weeks from tomorrow (today too when the visit is later), the day they were asked about left out;
  // any other date from "Pick…".
  const drawDays = (picked: string | null): void => {
    clear(s.body)
    s.body.append(el('p', { class: 'they-replied-about' }, `Which day did ${firstNames(cl)} agree?`))
    const today = todayISO()
    const start = cl.date > today ? today : addDaysISO(today, 1)
    const grid = el('div', { class: 'they-replied-days', role: 'group', 'aria-label': 'Days' })
    let month = ''
    for (let i = 0; i < 21; i++) {
      const iso = addDaysISO(start, i)
      if (iso === cl.date) continue
      const d = new Date(`${iso}T12:00:00`)
      const m = d.toLocaleDateString('en-GB', { month: 'short' })
      const info = o.dayInfo?.(iso) ?? null
      const b = el('button', { type: 'button', class: `they-replied-day${picked === iso ? ' is-on' : ''}`, 'aria-pressed': String(picked === iso), 'aria-label': `${dayLabel(iso)}${info ? `, ${info}` : ''}` },
        el('span', {}, d.toLocaleDateString('en-GB', { weekday: 'short' })), el('b', {}, String(d.getDate())), el('small', {}, info ?? (m !== month ? m : '')))
      month = m
      b.addEventListener('click', () => { buzz('light'); drawDays(iso) })
      grid.append(b)
    }
    const pick = el('input', { type: 'date', class: 'they-replied-pick', min: today, 'aria-label': 'Pick another date' }) as HTMLInputElement
    if (picked && !grid.querySelector('.is-on')) pick.value = picked
    pick.addEventListener('change', () => { if (pick.value && pick.value >= today && pick.value !== cl.date) drawDays(pick.value) })
    s.body.append(grid, el('label', { class: 'they-replied-pick-row' }, el('span', {}, 'Or pick a date'), pick))
    if (time) {
      const tick = el('input', { type: 'checkbox', id: `they-replied-keep-${cl.id}` }) as HTMLInputElement
      tick.checked = keep
      tick.addEventListener('change', () => { keep = tick.checked })
      s.body.append(el('label', { class: 'they-replied-keep', for: tick.id }, tick, `Keep ${shownTime(time ?? '')}`))
    }
    const go = el('button', { type: 'button', class: 'btn-primary they-replied-go' }, picked ? `Move to ${shortDay(picked)}, agreed` : 'Pick the day they agreed') as HTMLButtonElement
    go.disabled = !picked
    go.addEventListener('click', () => { if (!picked) return; s.close(); agreeOnDay(cl, picked, keep, o) })
    const back = el('button', { type: 'button', class: 'link-button they-replied-back' }, 'Back')
    back.addEventListener('click', draw)
    s.body.append(go, back)
    // The keyboard's place: the button once a day is picked, else the first day.
    if (picked) go.focus({ preventScroll: true })
    else grid.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true })
  }
  draw()
}

// A drag (or Move) of a visit asked about its day, onto another day: did they agree that day? Yes: moved there, agreed.
// No: moved as before, and they are asked about it (the move's own Undo bar offers Message them). Cancel: not moved.
export function askAgreedFirst(cl: Clean, iso: string, answer: (agreed: boolean) => void): void {
  const s = sheet(`Did ${firstNames(cl)} agree ${dayLabel(iso)}?`)
  s.body.append(el('p', { class: 'they-replied-about' }, `They were asked about ${dayLabel(cl.date)}.`))
  const yes = bigButton(`Yes, they agreed ${shortDay(iso)}`, 'Moved there, agreed. No message goes.', true)
  yes.addEventListener('click', () => { s.close(); answer(true) })
  const no = bigButton('No, I\'ll ask them', 'Moved there, not agreed. Their message for that day opens next.')
  no.addEventListener('click', () => { s.close(); answer(false) })
  const cancel = el('button', { type: 'button', class: 'link-button they-replied-back' }, 'Cancel, leave it where it is')
  cancel.addEventListener('click', s.close)
  s.body.append(yes, no, cancel)
  yes.focus({ preventScroll: true })
}
